import styled from "styled-components";
import { Button } from "primereact/button";
import { theme } from "styles/theme";

// Containers
export const PageContainer = styled.div`
  padding: 1rem 0;
  height: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const PageTitle = styled.h1`
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

export const ContentContainer = styled.div`
  background-color: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  padding: 1.5rem;
  color: ${theme.colors.textPrimary};
`;

export const DetailsTabView = styled.div`
  .p-tabview-nav {
    background: ${theme.colors.bgLight};
    border-bottom: 1px solid ${theme.colors.border};
    margin: 0 0 0.5rem;
  }

  .p-tabview-panels {
    background: ${theme.colors.surface};
    color: ${theme.colors.textPrimary};
    padding-top: 1rem;
  }

  .p-tabview-nav li .p-tabview-nav-link {
    color: ${theme.colors.textSecondary};
  }

  .p-tabview-nav li.p-highlight .p-tabview-nav-link {
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.surface};
    box-shadow: inset 0 -2px 0 var(--davey-section-accent);
  }
`;

// User info section
export const UserInfoSection = styled.div`
  margin-bottom: 2rem;
`;

export const UserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const UserName = styled.h2`
  color: ${theme.colors.textPrimary};
  margin: 0;
  font-weight: ${theme.fontWeights.medium};
`;

export const BadgesRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

export const UserInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
`;

export const InfoCard = styled.div`
  padding: 1rem;
  border-radius: ${theme.borderRadius.small};
  background-color: ${theme.colors.bgLight};
  border-left: 4px solid var(--davey-section-accent);
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
`;

export const InfoLabel = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
  margin-bottom: 0.5rem;
`;

export const InfoValue = styled.div`
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.fontWeights.medium};
  word-break: break-word;
`;

// Status badges
export const StatusBadge = styled.span<{ status: string }>`
  background-color: ${({ status }) =>
    status === "active"
      ? theme.colors.statusActive
      : status === "disabled"
        ? theme.colors.statusInactive
        : theme.colors.lightGrey};
  color: ${({ status }) =>
    status === "active"
      ? theme.colors.statusActiveText
      : status === "disabled"
        ? theme.colors.statusInactiveText
        : theme.colors.darkGrey};
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: ${theme.fontWeights.medium};
`;

export const RoleBadge = styled.span<{ role: string }>`
  background-color: ${({ role }) =>
    role === "ADMIN"
      ? theme.colors.tertiary
      : role === "DEALER"
        ? theme.colors.primary
        : theme.colors.lightGrey};
  color: ${({ role }) =>
    role === "ADMIN"
      ? theme.colors.onTertiary
      : role === "DEALER"
        ? theme.colors.onPrimary
        : theme.colors.textPrimary};
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: ${theme.fontWeights.medium};
  text-transform: capitalize;
`;

// Password management section
export const PasswordSection = styled.div`
  margin-top: 0;
`;

export const SectionTitle = styled.h3`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.large};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PasswordActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
`;

export const ActionCard = styled.div`
  padding: 1.5rem;
  border-radius: ${theme.borderRadius.medium};
  background-color: ${theme.colors.bgLight};
  border: 1px solid ${theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ActionCardTitle = styled.h4`
  color: ${theme.colors.textPrimary};
  margin: 0;
  font-size: ${theme.fontSizes.regular};
  font-weight: ${theme.fontWeights.medium};
`;

export const ActionCardDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
  margin: 0;
  line-height: 1.5;
`;

export const PasswordInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const InputLabel = styled.label`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.medium};
`;

export const PasswordStrengthHint = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
`;

// Edit form styles
export const EditFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const FormLabel = styled.label`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.medium};
`;

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

export const CheckboxLabel = styled.label`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.regular};
  cursor: pointer;
`;

export const CheckboxesSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: ${theme.colors.bgLight};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.borderLight};
`;

export const FormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

// Device section
export const DeviceSection = styled.div`
  margin-top: 0;
`;

export const DeviceCard = styled.div`
  padding: 1.5rem;
  border-radius: ${theme.borderRadius.medium};
  background-color: ${theme.colors.bgLight};
  border: 1px solid ${theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 480px;
`;

export const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.border};
`;

export const DeviceSerial = styled.span`
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.regular};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${theme.colors.tertiary};
  }
`;

export const DeviceInputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
`;

// Navigation and buttons
export const BackButton = styled(Button)`
  background-color: transparent;
  border: 1px solid ${theme.colors.tertiary};
  color: ${theme.colors.textPrimary};

  &:hover {
    background-color: ${theme.colors.tertiary};
    color: ${theme.colors.onTertiary};
  }
`;
